<script setup>
/* eslint-disable camelcase */
import CustomConfirmationDialog from '@/components/common/CustomConfirmationDialog.vue'
import CustomDatatable from '@/components/common/CustomDatatable.vue'
import CustomForm from '@/components/common/CustomForm.vue'
import axios from '@/utils/axios'
import { toast } from 'vue3-toastify'

const branchCodes = ref({ data: [], total: 0 })
const route = useRoute()
const companies = ref([])
const branches = ref([])
const filters = ref({ page: 1, perPage: 10 })
const formRef = ref()
const formDialogVisible = ref(false)
const viewDialogVisible = ref(false)
const deleteDialogVisible = ref(false)
const saving = ref(false)
const selectedBranchCode = ref(null)
const formData = ref(emptyForm())

const permissions = Object.values(JSON.parse(localStorage.getItem('userData') || '{}').permissions_by_roles || {}).flat()
const canCreate = permissions.includes('Create Branch Code')
const canEdit = permissions.includes('Edit Branch Code')
const canDelete = permissions.includes('Delete Branch Code')

const headers = [
  { title: 'Branch Code', key: 'branch_code', sortable: true },
  { title: 'Investment Company', key: 'investment_company', sortable: true },
  { title: 'Assigned Branches', key: 'branches_count', sortable: true },
  { title: 'Linked Rep Codes', key: 'rep_codes_count', sortable: true },
  { title: 'Description / Notes', key: 'description', sortable: true },
  { title: 'Actions', key: 'actions', sortable: false },
]

function emptyForm() {
  return { id: null, branch_code: '', investment_company_id: null, description: '', branch_ids: [] }
}

const companyOptions = computed(() => companies.value.map(company => ({ title: company.title, value: company.id })))
const branchOptions = computed(() => branches.value.map(branch => ({ title: branch.branch_name, value: branch.id })))

const branchCodeRules = [
  value => !!value || 'Branch code is required',
  value => /^[A-Za-z0-9]+$/.test(value || '') || 'Use letters and numbers only',
  value => String(value || '').length <= 12 || 'Maximum 12 characters',
]

async function fetchReferenceData() {
  const [companyResponse, branchResponse] = await Promise.all([
    axios.get('/investment_companies?all=1'),
    axios.get('/branches?per_page=250'),
  ])

  if (companyResponse.status === 'success')
    companies.value = companyResponse.data.investment_companies || []
  if (branchResponse.status === 'success')
    branches.value = branchResponse.data.branches?.data || branchResponse.data.branches || []
}

async function fetchBranchCodes(payload = filters.value) {
  filters.value = { ...filters.value, ...payload }

  const response = await axios.get('/branch_codes', { params: {
    page: filters.value.page || 1,
    per_page: filters.value.perPage || 10,
    search_query: filters.value.searchQuery || undefined,
    sort_by: filters.value.sortBy || undefined,
    order_by: filters.value.orderBy || undefined,
    investment_company_id: filters.value.investmentCompanyId || undefined,
  } })

  if (response.status === 'success')
    branchCodes.value = response.data.branch_codes
  else
    toast.error(response.message)
}

function addItem() {
  formData.value = emptyForm()
  formData.value.investment_company_id = route.query.investment_company_id ? Number(route.query.investment_company_id) : null
  formDialogVisible.value = true
}

function editItem(item) {
  formData.value = {
    id: item.id,
    branch_code: item.branch_code,
    investment_company_id: item.investment_company_id,
    description: item.description || '',
    branch_ids: [...(item.branch_ids || [])],
  }
  formDialogVisible.value = true
}

async function submitForm() {
  const validation = await formRef.value?.validate()
  if (!validation?.valid)
    return

  saving.value = true
  try {
    const payload = { ...formData.value, branch_code: formData.value.branch_code.trim().toUpperCase() }

    const response = payload.id
      ? await axios.put(`/branch_codes/${payload.id}`, payload)
      : await axios.post('/branch_codes', payload)

    if (response.status === 'success') {
      toast.success(response.message)
      formDialogVisible.value = false
      await fetchBranchCodes()
    } else {
      toast.error(response.message)
    }
  } finally {
    saving.value = false
  }
}

async function confirmDelete(confirmed) {
  if (!confirmed) {
    deleteDialogVisible.value = false
    
    return
  }
  const response = await axios.delete(`/branch_codes/${selectedBranchCode.value.id}`)
  if (response.status === 'success') {
    toast.success(response.message)
    deleteDialogVisible.value = false
    await fetchBranchCodes()
  } else {
    toast.error(response.message)
  }
}

onMounted(async () => {
  if (route.query.investment_company_id)
    filters.value.investmentCompanyId = Number(route.query.investment_company_id)
  await Promise.all([fetchReferenceData(), fetchBranchCodes()])
})

definePage({ meta: { requiresAuth: true } })
</script>

<template>
  <div>
    <div class="mb-4">
      <h3 class="text-h3 text-high-emphasis mb-1">
        Branch Codes
      </h3>
      <div>Manage investment-company branch codes and their branch assignments.</div>
    </div>

    <VCard>
      <VCardText class="pb-0">
        <AppSelect
          v-model="filters.investmentCompanyId"
          :items="companyOptions"
          label="Investment Company"
          placeholder="All investment companies"
          clearable
          style="max-inline-size: 360px"
          @update:model-value="fetchBranchCodes({ ...filters, page: 1, investmentCompanyId: $event })"
        />
      </VCardText>
      <VCardText class="pa-0">
        <CustomDatatable
          :headers="headers"
          :table-data="branchCodes"
          @table-changed="fetchBranchCodes"
          @search-value="searchQuery => fetchBranchCodes({ ...filters, page: 1, searchQuery })"
        >
          <template #add_button>
            <VBtn
              v-if="canCreate"
              prepend-icon="tabler-plus"
              @click="addItem"
            >
              Add Branch Code
            </VBtn>
          </template>
          <template #item.branch_code="{ item }">
            <span class="font-weight-medium text-primary">{{ item.branch_code }}</span>
          </template>
          <template #item.investment_company="{ item }">
            {{ item.investment_company?.title || '—' }}
          </template>
          <template #item.branches_count="{ item }">
            <VChip
              v-for="branch in item.branches"
              :key="branch.id"
              size="small"
              color="primary"
              variant="tonal"
              class="me-1 mb-1"
            >
              {{ branch.name }}
            </VChip>
            <span
              v-if="!item.branches?.length"
              class="text-medium-emphasis"
            >—</span>
          </template>
          <template #item.rep_codes_count="{ item }">
            <VChip
              size="small"
              variant="tonal"
            >
              {{ item.rep_codes_count }}
            </VChip>
          </template>
          <template #item.description="{ item }">
            <span class="text-wrap">{{ item.description || '—' }}</span>
          </template>
          <template #table_actions="{ item }">
            <div class="d-flex gap-1">
              <VBtn
                size="x-small"
                color="primary"
                @click="selectedBranchCode = item; viewDialogVisible = true"
              >
                <VIcon icon="tabler-eye" />
              </VBtn>
              <VBtn
                v-if="canEdit"
                size="x-small"
                color="primary"
                @click="editItem(item)"
              >
                <VIcon icon="tabler-pencil" />
              </VBtn>
              <VBtn
                v-if="canDelete"
                size="x-small"
                color="error"
                @click="selectedBranchCode = item; deleteDialogVisible = true"
              >
                <VIcon icon="tabler-trash" />
              </VBtn>
            </div>
          </template>
        </CustomDatatable>
      </VCardText>
    </VCard>

    <CustomForm
      v-model:is-dialog-visible="formDialogVisible"
      :is-dialog-visible="formDialogVisible"
      :title="formData.id ? 'Edit Branch Code' : 'Add Branch Code'"
      description="Set the investment company and assign one or more branches."
      :width="700"
    >
      <template #form>
        <VForm
          ref="formRef"
          class="mt-6"
          @submit.prevent="submitForm"
        >
          <VRow>
            <VCol
              cols="12"
              md="6"
            >
              <AppTextField
                v-model="formData.branch_code"
                label="Branch Code *"
                placeholder="Up to 12 letters or numbers"
                maxlength="12"
                :counter="12"
                :rules="branchCodeRules"
                @update:model-value="formData.branch_code = String($event || '').toUpperCase()"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <AppSelect
                v-model="formData.investment_company_id"
                :items="companyOptions"
                label="Investment Company *"
                placeholder="Select a company"
                :rules="[value => !!value || 'Investment company is required']"
              />
            </VCol>
            <VCol cols="12">
              <AppCombobox
                v-model="formData.branch_ids"
                :items="branchOptions"
                label="Assigned Branches"
                placeholder="Select one or more branches"
                multiple
                chips
                closable-chips
                :return-object="false"
              />
            </VCol>
            <VCol cols="12">
              <AppTextarea
                v-model="formData.description"
                label="Description / Notes"
                placeholder="Optional notes"
                rows="4"
              />
            </VCol>
            <VCol
              cols="12"
              class="d-flex justify-end gap-3"
            >
              <VBtn
                color="secondary"
                variant="tonal"
                @click="formDialogVisible = false"
              >
                Cancel
              </VBtn>
              <VBtn
                type="submit"
                :loading="saving"
              >
                {{ formData.id ? 'Update' : 'Create' }}
              </VBtn>
            </VCol>
          </VRow>
        </VForm>
      </template>
    </CustomForm>

    <VDialog
      v-model="viewDialogVisible"
      max-width="650"
    >
      <VCard title="Branch Code Details">
        <VCardText v-if="selectedBranchCode">
          <VList>
            <VListItem
              title="Branch Code"
              :subtitle="selectedBranchCode.branch_code"
            />
            <VListItem
              title="Investment Company"
              :subtitle="selectedBranchCode.investment_company?.title || '—'"
            />
            <VListItem
              title="Description / Notes"
              :subtitle="selectedBranchCode.description || '—'"
            />
            <VListItem
              title="Linked Rep Codes"
              :subtitle="String(selectedBranchCode.rep_codes_count || 0)"
            />
          </VList>
          <div class="text-subtitle-1 font-weight-medium mt-3 mb-2">
            Assigned Branches
          </div>
          <VChip
            v-for="branch in selectedBranchCode.branches"
            :key="branch.id"
            class="me-2 mb-2"
            color="primary"
            variant="tonal"
          >
            {{ branch.name }}
          </VChip>
          <div
            v-if="!selectedBranchCode.branches?.length"
            class="text-medium-emphasis"
          >
            No branches assigned.
          </div>
        </VCardText>
        <VCardActions class="justify-end">
          <VBtn
            variant="tonal"
            @click="viewDialogVisible = false"
          >
            Close
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <CustomConfirmationDialog
      :is-dialog-visible="deleteDialogVisible"
      title="Delete this branch code?"
      description="Branch assignments will be removed and linked rep codes will be safely unlinked."
      icon="tabler-exclamation-circle"
      icon-color="error"
      confirm-button-text="Delete"
      confirm-button-color="error"
      cancel-button-text="Cancel"
      @confirmation="confirmDelete"
    />
  </div>
</template>
